import React, { useState, useRef, useEffect } from "react";
import { DAppClient, NetworkType } from "@airgap/beacon-sdk";
import { TezosToolkit } from "@taquito/taquito";
import CustomToastContainer from "../../Common/CustomToastContainer";
import { BeaconWallet } from "@taquito/beacon-wallet";
const domainpath = process.env.REACT_APP_API_DOMAIN_ENDPOINT;
const tezos = new TezosToolkit(process.env.REACT_APP_RPC_URL); // or use a custom RPC endpoint

const wallet = new BeaconWallet({
  name: "ArtCollect",
  preferredNetwork: NetworkType.GHOSTNET, // or GHOSTNET
});

tezos.setWalletProvider(wallet);
const EditProfile = ({}) => {
  // Tab state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Profile");
  const toastRef = useRef();
  // Wallet state
  const [walletAddress, setWalletAddress] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Profile images
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerImageFile, setBannerImageFile] = useState(null);

  // Form state
  const [formState, setFormState] = useState({
    username: "",
    customUrl: "",
    bio: "",
    email: "",
    website: "",
    twitter: "",
    instagram: "",
  });

  // Notifications state
  const [notificationSettings, setNotificationSettings] = useState({
    itemSold: true,
    bidActivity: true,
    priceChange: true,
    auctionEnded: true,
    outbid: true,
    successfulPurchase: true,
  });

  const profileInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // Check for wallet address on mount
  useEffect(() => {
    const storedAddress = localStorage.getItem("walletAddress");
    if (storedAddress) {
      setWalletAddress(storedAddress);
    }
  }, []);

  const handleImageChange = (e, setImage, setFile) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImage(imageUrl);
      setFile(file);
    }
  };
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${domainpath}/api/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      // console.log(data, "data");

      if (!response.ok) {
        throw new Error(data.error || "Image upload failed");
      }

      return data.publicUrl; // assuming the backend returns { imageUrl: "https://..." }
    } catch (error) {
      console.error("Image upload error:", error);
      toastRef.current.addToast(error.message, "error");
      return null;
    }
  };

  const connectWallet = async () => {
    setIsConnecting(true);
    try {
      // Check if Tezos wallet is available
      if (!window.tezos) {
        throw new Error("Tezos wallet extension not found");
      }

      // Request permissions
      await window.tezos.request({ method: "eth_requestAccounts" });

      // Get active account
      const tezos = new TezosToolkit(process.env.REACT_APP_RPC_URL);
      const activeAccount = await tezos.wallet.pkh();

      // Store wallet address
      localStorage.setItem("walletAddress", activeAccount);
      setWalletAddress(activeAccount);

      toastRef.current.addToast("Wallet connected successfully!", "success");
    } catch (error) {
      console.error("Wallet connection error:", error);
      let errorMessage = "Failed to connect wallet";
      if (error.message.includes("extension not found")) {
        errorMessage = "Tezos wallet extension not installed";
      } else if (
        error.message.includes("rejected") ||
        error.message.includes("cancelled")
      ) {
        errorMessage = "Wallet connection was cancelled";
      }
      toastRef.current.addToast(errorMessage, "error");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNotificationToggle = (setting) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };
  useEffect(() => {
    const storedAddress = localStorage.getItem("walletAddress");
    if (storedAddress) {
      setWalletAddress(storedAddress);

      const fetchProfileData = async () => {
        try {
          const response = await fetch(
            `${domainpath}/api/profile?walletAddress=${storedAddress}`
          );
          const data = await response.json();

          if (data.success === true) {
            const profile = data.profile;

            setFormState({
              username: profile.username || "",
              customUrl: profile.customUrl || "",
              bio: profile.bio || "",
              email: profile.email || "",
              website: profile.website || "",
              twitter: profile.twitter || "",
              instagram: profile.instagram || "",
            });

            setNotificationSettings({
              itemSold: profile.notifications?.itemSold ?? true,
              bidActivity: profile.notifications?.bidActivity ?? true,
              priceChange: profile.notifications?.priceChange ?? true,
              auctionEnded: profile.notifications?.auctionEnded ?? true,
              outbid: profile.notifications?.outbid ?? true,
              successfulPurchase:
                profile.notifications?.successfulPurchase ?? true,
            });
            setProfileImageFile(profile.profileImage);
            setBannerImageFile(profile.bannerImage);
            if (profile.profileImage) setProfileImage(profile.profileImage);
            if (profile.bannerImage) setBannerImage(profile.bannerImage);
          } else {
            console.warn("Failed to fetch profile data:", data.error);
          }
        } catch (err) {
          console.error("Profile fetch error:", err);
        }
      };

      fetchProfileData();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(walletAddress);

    if (!walletAddress) {
      toastRef.current.addToast("Please connect your wallet first", "warning");
      return;
    }

    setLoading(true);

    try {
      let profileImageUrl = profileImage || null;
      let bannerImageUrl = bannerImage || null;

      // Upload only if a new file is selected (i.e., user has changed the image)
      if (profileImageFile && profileImageFile !== profileImage) {
        profileImageUrl = await uploadImage(profileImageFile);
        if (!profileImageUrl) throw new Error("Profile image upload failed");
      }

      if (bannerImageFile && bannerImageFile !== bannerImage) {
        bannerImageUrl = await uploadImage(bannerImageFile);
        if (!bannerImageUrl) throw new Error("Banner image upload failed");
      }

      const formData = {
        walletAddress,
        ...formState,
        notifications: notificationSettings,
        profileImage: profileImageUrl,
        bannerImage: bannerImageUrl,
      };

      const response = await fetch(`${domainpath}/api/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Profile update failed");
      }

      toastRef.current.addToast("Profile updated successfully!", "success");
      // console.log("Profile updated:", data.profile);
      setFormState({
        username: "",
        customUrl: "",
        bio: "",
        email: "",
        website: "",
        twitter: "",
        instagram: "",
      });
      setNotificationSettings({
        itemSold: false,
        bidActivity: false,
        priceChange: false,
        auctionEnded: false,
        outbid: false,
        successfulPurchase: false,
      });
      setBannerImage(null);
      setProfileImage(null);
      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (error) {
      console.error("Update error:", error);
      toastRef.current.addToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="jumbotron breadcumb no-bg">
        <div className="mainbreadcumb">
          <div className="container">
            <div className="row m-10-hor">
              <div className="col-12">
                <h1 className="text-center">EDIT PROFILE</h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="">
        <div className="">
          <div className="container p-6">
            <div className="col-lg-12 mb-5 offset-lg-1">
              <div className="items_filter">
                <ul className="de_nav text-left">
                  <li className={activeTab === "Profile" ? "active" : ""}>
                    <span onClick={() => setActiveTab("Profile")}>
                      <i className="fa fa-user"></i>Profile
                    </span>
                  </li>
                  <li className={activeTab === "Notifications" ? "active" : ""}>
                    <span onClick={() => setActiveTab("Notifications")}>
                      <i className="fa fa-exclamation-circle"></i>
                      Notifications
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <form
              id="form-create-item"
              className="form-border"
              onSubmit={(e) => handleSubmit(e)}
            >
              <div className="space-y-6">
                {/* Profile Tab */}
                {activeTab === "Profile" && (
                  <div className="row">
                    {/* Left Column - Profile Fields */}
                    <div className="col-lg-5 offset-lg-1">
                      <div className="space-y-4">
                        <h5 className="text-xl font-semibold">USERNAME</h5>
                        <input
                          type="text"
                          name="username"
                          placeholder="Enter username"
                          className="form-control"
                          value={formState.username}
                          onChange={handleInputChange}
                        />

                        <h5 className="text-xl font-semibold">CUSTOM URL</h5>
                        <input
                          type="text"
                          name="customUrl"
                          placeholder="Enter your custom URL"
                          className="form-control"
                          value={formState.customUrl}
                          onChange={handleInputChange}
                        />

                        <h5 className="text-xl font-semibold">BIO</h5>
                        <textarea
                          name="bio"
                          placeholder="Tell the world who you are!"
                          className="form-control"
                          value={formState.bio}
                          onChange={handleInputChange}
                        ></textarea>

                        <h5 className="text-xl font-semibold">
                          EMAIL ADDRESS*
                        </h5>
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter email"
                          className="form-control"
                          value={formState.email}
                          onChange={handleInputChange}
                        />

                        <h5 className="text-xl font-semibold">
                          <i className="fa fa-link"></i> YOUR SITE
                        </h5>
                        <input
                          type="text"
                          name="website"
                          placeholder="Enter Website URL"
                          className="form-control"
                          value={formState.website}
                          onChange={handleInputChange}
                        />

                        <h5 className="text-xl font-semibold">
                          <i className="fa fa-twitter"></i> TWITTER USERNAME
                        </h5>
                        <input
                          type="text"
                          name="twitter"
                          placeholder="Enter Twitter username"
                          className="form-control"
                          value={formState.twitter}
                          onChange={handleInputChange}
                        />

                        <h5 className="text-xl font-semibold">
                          <i className="fa fa-instagram"></i> INSTAGRAM USERNAME
                        </h5>
                        <input
                          type="text"
                          name="instagram"
                          placeholder="Enter Instagram username"
                          className="form-control"
                          value={formState.instagram}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Right Column - Upload Section */}
                    <div className="col-lg-4">
                      <div className="space-y-4 pl-4">
                        <h2 className="text-xl font-semibold">Upload photo</h2>
                        <div className="border-b border-gray-200 mb-4"></div>

                        {/* Profile Image */}
                        <div className="space-y-4">
                          <h3 className="text-lg font-medium">
                            PROFILE IMAGE{" "}
                            <i
                              className="fa fa-info-circle id-color-2"
                              title="Recommend 400 x 400. Max size: 50MB. Click the image to upload."
                            ></i>
                          </h3>
                          <div
                            className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center overflow-hidden cursor-pointer hover:border-black"
                            onClick={() => profileInputRef.current.click()}
                          >
                            {profileImage ? (
                              <img
                                src={profileImage}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-400 text-2xl">+</span>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              ref={profileInputRef}
                              onChange={(e) =>
                                handleImageChange(
                                  e,
                                  setProfileImage,
                                  setProfileImageFile
                                )
                              }
                              className="hidden"
                            />
                          </div>
                        </div>

                        {/* Banner Image */}
                        <div className="space-y-4 mt-6">
                          <h3 className="text-lg font-medium">
                            PROFILE BANNER{" "}
                            <i
                              className="fa fa-info-circle id-color-2"
                              title="Recommend 1400 x 400. Max size: 50MB. Click the image to upload."
                            ></i>
                          </h3>
                          <div
                            className="w-full h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center overflow-hidden cursor-pointer hover:border-black"
                            onClick={() => bannerInputRef.current.click()}
                          >
                            {bannerImage ? (
                              <img
                                src={bannerImage}
                                alt="Banner"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-400 text-2xl">+</span>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              ref={bannerInputRef}
                              onChange={(e) =>
                                handleImageChange(
                                  e,
                                  setBannerImage,
                                  setBannerImageFile
                                )
                              }
                              className="hidden"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notifications Tab */}
                {activeTab === "Notifications" && (
                  <div className="space-y-6 offset-lg-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(notificationSettings).map(
                        ([key, value]) => {
                          const notificationTitles = {
                            itemSold: "Item Sold",
                            bidActivity: "Bid Activity",
                            priceChange: "Price Change",
                            auctionEnded: "Auction Ended",
                            outbid: "Outbid",
                            successfulPurchase: "Successful Purchase",
                          };

                          const notificationDescriptions = {
                            itemSold: "When someone purchased your item.",
                            bidActivity:
                              "When someone places a bid on your item.",
                            priceChange:
                              "When an item you made an offer on changes in price.",
                            auctionEnded: "When an auction you created ends.",
                            outbid:
                              "When an offer you placed is exceeded by another user.",
                            successfulPurchase:
                              "When you successfully buy an item.",
                          };

                          return (
                            <div key={key} className="p-4 switch-with-title s2">
                              <div className="flex justify-between items-center mb-1">
                                <h5 className="text-md font-semibold">
                                  {notificationTitles[key]}
                                </h5>
                                <div className="de-switch">
                                  <input
                                    type="checkbox"
                                    id={`notif-${key}`}
                                    className="checkbox hidden peer"
                                    checked={value}
                                    onChange={() =>
                                      handleNotificationToggle(key)
                                    }
                                  />
                                  <label
                                    htmlFor={`notif-${key}`}
                                    className={`block w-11 h-6 rounded-full relative cursor-pointer transition ${
                                      value ? "bg-blue-600" : "bg-gray-200"
                                    }`}
                                  ></label>
                                </div>
                              </div>
                              <p className="text-sm text-gray-500 p-info">
                                {notificationDescriptions[key]}
                              </p>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

                <div className="col-12 mt-4 offset-lg-1">
                  <button
                    type="submit"
                    className="btn-main"
                    //disabled={!walletAddress}
                  >
                    Update Profile
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
      <CustomToastContainer ref={toastRef} />
      {loading && (
        <div className="RoadmapSpinnerDiv" id="RoadmapSpinnerDiv">
          <span className="RoadmapSpinner"></span>
        </div>
      )}
    </>
  );
};

export default EditProfile;
